// src/pages/VolpeTrackPrivacyPolicy.js

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiGlobe, FiChevronLeft } from 'react-icons/fi';
import SEO from '../components/SEO';

// Styled components
const PageContainer = styled.div`
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(135deg, #1A365D 0%, #0B1D3A 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.03) 25%, transparent 25%),
      linear-gradient(45deg,  rgba(255,255,255,0.03) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.4;
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  width: 100%;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: var(--spacing-xl) var(--spacing-md);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(135deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(225deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(315deg, rgba(255,255,255,0.05) 25%, transparent 25%),
      linear-gradient(45deg,  rgba(255,255,255,0.05) 25%, transparent 25%);
    background-size: 60px 60px;
    opacity: 0.5;
    z-index: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--white);
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const LastUpdated = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const PolicyContainer = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(4px);

  @media (max-width: 768px) {
    padding: var(--spacing-lg);
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-top: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  color: var(--secondary);
  border-bottom: 2px solid var(--accent);
  padding-bottom: 0.5rem;

  &:first-of-type {
    margin-top: 0;
  }
`;

const PolicyText = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-md);
`;

const List = styled.ul`
  margin-bottom: var(--spacing-md);
  padding-left: var(--spacing-lg);
`;

const ListItem = styled.li`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-xs);
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  cursor: pointer;
  background: none;
  border: none;
  text-decoration: none;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-sm);
  backdrop-filter: blur(8px);
  transition: all var(--transition-fast);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10;

  svg { margin-right: var(--spacing-xs); }

  &:hover {
    color: var(--white);
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
    transform: translateY(-2px);
  }
`;

const LanguageToggle = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: var(--border-radius-md);
  padding: 0.5rem 0.75rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 5;

  &:hover {
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: var(--shadow-md);
  }
`;

const LanguageIcon = styled.div`
  margin-right: var(--spacing-xs);
  display: flex;
  align-items: center;
  color: var(--white);
`;

const LanguageText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--white);
`;

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const VolpeTrackPrivacyPolicy = () => {
  // Update to deployment date if needed
  const lastUpdated = "Oct 25, 2025";

  // EN default for production
  const [language, setLanguage] = useState('en');

  const toggleLanguage = () => setLanguage(language === 'en' ? 'es' : 'en');

  const handleBackClick = () => {
    window.location.href = '/';
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <SEO routeKey="volpeTrackPrivacy" />
      <PageContainer>
        <ContentWrapper>
        <TopBar>
          <BackButton onClick={handleBackClick}>
            <FiChevronLeft /> {language === 'en' ? 'Back to Home' : 'Volver al inicio'}
          </BackButton>
          <LanguageToggle onClick={toggleLanguage}>
            <LanguageIcon><FiGlobe /></LanguageIcon>
            <LanguageText>{language === 'en' ? 'Español' : 'English'}</LanguageText>
          </LanguageToggle>
        </TopBar>

        <PageHeader>
          <PageTitle>
            {language === 'en' ? 'VolpeTrack Privacy Policy' : 'Política de Privacidad de VolpeTrack'}
          </PageTitle>
          <LastUpdated>
            {language === 'en'
              ? `Last updated: ${lastUpdated}`
              : `Última actualización: ${lastUpdated}`}
          </LastUpdated>
        </PageHeader>

        {language === 'en' ? (
          <PolicyContainer initial="hidden" animate="visible" variants={fadeIn}>
            <PolicyText>
              VolpeTrack is developed by <strong>XADevs LLC</strong> (“VolpeTrack”, “we”). We respect your privacy.
              This Policy explains what data we process, for what purposes and legal bases, which providers we use,
              how long we keep data, and the choices and rights you have.
            </PolicyText>

            <SectionTitle>1. Data We Process</SectionTitle>
            <List>
              <ListItem><strong>Account / Authentication</strong>: name, email, and profile photo via <strong>Google Sign-In</strong>, <strong>Apple Sign-In</strong>, and <strong>email + password</strong> (Firebase Auth or an equivalent provider).</ListItem>
              <ListItem><strong>Financial data you input</strong>: income, expenses, categories, goals, notes, and related records to operate the service.</ListItem>
              <ListItem><strong>Preferences & usage</strong>: language, currency, theme, achievements, notification settings, basic interaction analytics.</ListItem>
              <ListItem><strong>Technical logs</strong>: device/app info, OS version, IP address, error/performance logs for security and diagnostics.</ListItem>
              <ListItem><strong>AI assistant content</strong>: text you send to the assistant to generate responses.</ListItem>
              <ListItem><strong>Voice transcripts & audio metadata (optional)</strong>: when you use speech-to-text, your voice input is processed by the OS speech service (Google/Apple) to create a transcript. We store the transcript to provide the feature. We do not keep raw audio.</ListItem>
              <ListItem><strong>Device identifiers (ads SDKs in Free plan only)</strong>: advertising identifier (GAID/IDFA), basic device/app signals, and aggregated ad events for frequency capping, measurement, and fraud prevention.</ListItem>
            </List>

            <SectionTitle>2. Sign-in Methods</SectionTitle>
            <PolicyText>
              Currently available: <strong>Google Sign-In</strong>, <strong>Apple Sign-In</strong>, and <strong>email + password</strong>
              (via Firebase Auth or an equivalent provider).
            </PolicyText>

            <SectionTitle>3. Purposes & Legal Bases</SectionTitle>
            <List>
              <ListItem><strong>Provide the service (contract)</strong>: sync and display your financial information and maintain your account.</ListItem>
              <ListItem><strong>AI assistant (contract/legitimate interest)</strong>: process your prompts to categorize and assist.</ListItem>
              <ListItem><strong>Improve & secure (legitimate interest)</strong>: metrics, diagnostics, fraud/abuse prevention.</ListItem>
              <ListItem><strong>Notifications (consent)</strong>: reminders via Firebase Cloud Messaging (FCM).</ListItem>
              <ListItem><strong>Marketing communications (consent)</strong>: only if you opt in; you can opt out anytime.</ListItem>
            </List>

            <SectionTitle>4. Third-Party Providers & Transfers</SectionTitle>
            <PolicyText>We use trusted providers and limit data to what is necessary:</PolicyText>
            <List>
              <ListItem><strong>Auth & Notifications</strong>: Google <em>Firebase Auth</em> (Google/Apple Sign-In and email + password) and <em>Firebase Cloud Messaging</em>.</ListItem>
              <ListItem><strong>Subscriptions & purchases</strong>: <em>RevenueCat</em> (payments via Google Play / App Store).</ListItem>
              <ListItem><strong>AI processing</strong>: <em>OpenAI</em>, <em>Azure OpenAI</em>, and/or <em>DeepSeek</em> (text processing only; not for ads; no model training with our data).</ListItem>
              <ListItem><strong>Profile image storage</strong>: <em>Bunny.net</em>.</ListItem>
              <ListItem><strong>Infrastructure</strong>: servers on <em>Linode</em> and <em>Microsoft Azure</em>.</ListItem>
              <ListItem><strong>Advertising (Free plan only)</strong>: <em>Unity Ads</em>, <em>AppLovin MAX</em>, and/or <em>Google AdMob</em> (NON-PERSONALIZED ads; see Section 10).</ListItem>
            </List>
            <PolicyText>
              <strong>Advertising identifier (AAID/IDFA)</strong>. Our ad SDKs (e.g., Unity Ads, AppLovin MAX, Google AdMob) may access the device's advertising identifier to serve non-personalized ads, apply frequency capping, measure performance, and prevent fraud/abuse. We do not use the identifier for personalized ads in the Free plan. You can reset or limit the identifier in your device settings.
            </PolicyText>
            <PolicyText>
              AI providers process user inputs solely to generate responses for the requested feature. We do not permit the use of your data for advertising, profiling, or model training, and we use contractual and technical controls to enforce this.
            </PolicyText>
            <PolicyText>
              Data may be transferred outside your country/EEA. Where required, we use Standard Contractual Clauses (SCCs) or other safeguards.
            </PolicyText>

            <SectionTitle>5. Retention</SectionTitle>
            <PolicyText>
              We keep your data while your account is active. After deletion, backups/logs are retained for <strong>60 days</strong> for security and compliance, then are irreversibly purged unless law requires otherwise.
            </PolicyText>

            <SectionTitle>6. Security</SectionTitle>
            <PolicyText>
              Encryption in transit and at rest, access controls, and secure development practices. No system is perfect; if a breach materially affects you, we will notify you as required by law.
            </PolicyText>

            <SectionTitle>7. Your Rights</SectionTitle>
            <PolicyText>
              Depending on your jurisdiction (e.g., GDPR/CCPA), you can request access, rectification, deletion, portability, restriction, objection, and withdraw consent. 
              Contact: <strong>privacy@xadevs.com</strong> from the email linked to your account.
            </PolicyText>

            <SectionTitle>8. Account Deletion & Data Export</SectionTitle>
            <PolicyText>
              You can request permanent account deletion from the app (or by email). Active data are erased; backups/logs follow the retention in Section 5. 
              Store providers may keep billing data under their own policies.
            </PolicyText>

            <SectionTitle>9. Age</SectionTitle>
            <PolicyText>
              VolpeTrack is intended for users aged 18 and over. We do not knowingly collect personal data from anyone under 18. If we discover that we have collected personal data from a minor, we will delete it and may terminate the account.
            </PolicyText>

            <SectionTitle>10. Ads, Plans & Consent</SectionTitle>
            <PolicyText>
              Plans: <strong>Free (Ad-Supported)</strong> shows advertising; <strong>Premium Pro</strong> and <strong>Founder Pioneer</strong> are ad-free.
            </PolicyText>
            <PolicyText>
              <strong>Non-personalized ads (Free plan only)</strong>. We show non-personalized ads. Ad partners may process the device advertising identifier (GAID/IDFA), coarse IP region, basic device/app signals, and aggregated ad events solely for frequency capping, measurement, and fraud prevention, in line with platform policies. We do not enable cross-app tracking or interest-based personalization in the Free plan. In the EEA/UK, if consent is required for specific ad SDK operations, we will request it in-app. Upgrading to Premium Pro or Founder Pioneer disables ad SDKs.
            </PolicyText>

            <SectionTitle>11. Permissions (Android)</SectionTitle>
            <PolicyText>
              We request only the permissions needed for the features you choose:
            </PolicyText>
            <List>
              <ListItem><strong>INTERNET</strong>: connect to our API and third-party services.</ListItem>
              <ListItem><strong>POST_NOTIFICATIONS</strong>: optional app notifications (you can disable them).</ListItem>
              <ListItem><strong>RECORD_AUDIO</strong>: optional speech-to-text for the AI assistant (only when you press the mic).</ListItem>
              <ListItem><strong>CAMERA</strong>: optional profile photo capture.</ListItem>
              <ListItem><strong>READ_EXTERNAL_STORAGE</strong> (Android 12 or earlier only, with maxSdkVersion=32): pick a profile photo from your gallery on older Android versions. On Android 13+, we use the Android Photo Picker and do not require gallery read access.</ListItem>
              <ListItem><strong>WAKE_LOCK</strong> and <strong>VIBRATE</strong>: reliable delivery and user feedback for notifications.</ListItem>
              <ListItem><strong>com.google.android.gms.permission.AD_ID</strong>: advertising identifier used by ad SDKs in the Free plan for non-personalized ads, measurement, and fraud prevention.</ListItem>
            </List>
            <PolicyText>
              We do not scan your photo library; we only process the single image you select for your avatar.
            </PolicyText>

            <SectionTitle>12. Purchases & Subscriptions</SectionTitle>
            <PolicyText>
              Payments are handled by Google Play / App Store via <strong>RevenueCat</strong>. We do not store card data on our servers.
              Charges appear as <strong>"XADevs LLC"</strong>. Manage or cancel subscriptions in your store account settings.
            </PolicyText>

            <SectionTitle>13. No Sale of Personal Data</SectionTitle>
            <PolicyText>
              We do not sell your personal data. For California residents, you can exercise "Do Not Sell or Share" by choosing non-personalized ads and/or contacting <strong>privacy@xadevs.com</strong>.
            </PolicyText>

            <SectionTitle>14. Changes to this Policy</SectionTitle>
            <PolicyText>
              We may update this Policy. We will notify significant changes in-app and, where appropriate, by email. The date at the top shows the latest revision.
            </PolicyText>

            <SectionTitle>15. Contact</SectionTitle>
            <PolicyText>
              Privacy: <strong>privacy@xadevs.com</strong><br/>
              Support: <strong>support@xadevs.com</strong><br/>
              Website: <strong>https://xadevs.com</strong>
            </PolicyText>
          </PolicyContainer>
        ) : (
          <PolicyContainer initial="hidden" animate="visible" variants={fadeIn}>
            <PolicyText>
              VolpeTrack es desarrollada por <strong>XADevs LLC</strong> (“VolpeTrack”, “nosotros”). Respetamos tu privacidad.
              Esta Política explica qué datos tratamos, con qué fines y bases legales, qué proveedores usamos, cuánto tiempo
              conservamos los datos y qué elecciones y derechos tienes.
            </PolicyText>

            <SectionTitle>1. Datos que tratamos</SectionTitle>
            <List>
              <ListItem><strong>Cuenta / Autenticación</strong>: nombre, correo y foto de perfil mediante <strong>Google Sign-In</strong>, <strong>Apple Sign-In</strong> y <strong>email + contraseña</strong> (Firebase Auth o proveedor equivalente).</ListItem>
              <ListItem><strong>Datos financieros aportados por ti</strong>: ingresos, gastos, categorías, metas, notas y registros necesarios para operar el servicio.</ListItem>
              <ListItem><strong>Preferencias y uso</strong>: idioma, moneda, tema, logros, ajustes de notificaciones y analítica básica de interacción.</ListItem>
              <ListItem><strong>Registros técnicos</strong>: info de dispositivo/app, versión de SO, IP, logs de error/rendimiento para seguridad y diagnóstico.</ListItem>
              <ListItem><strong>Contenido para el asistente de IA</strong>: texto que envías al asistente para generar respuestas.</ListItem>
              <ListItem><strong>Transcripciones de voz y metadatos de audio (opcional)</strong>: cuando usas voz a texto, tu entrada de voz es procesada por el servicio de voz del SO (Google/Apple) para crear una transcripción. Almacenamos la transcripción para ofrecer la función. No guardamos el audio sin procesar.</ListItem>
              <ListItem><strong>Identificadores de dispositivo (SDKs de anuncios solo en plan gratuito)</strong>: identificador de publicidad (GAID/IDFA), señales básicas de dispositivo/app y eventos de anuncios agregados para limitación de frecuencia, medición y prevención de fraude.</ListItem>
            </List>

            <SectionTitle>2. Métodos de inicio de sesión</SectionTitle>
            <PolicyText>
              Disponibles actualmente: <strong>Google Sign-In</strong>, <strong>Apple Sign-In</strong> y <strong>email + contraseña</strong>
              (a través de Firebase Auth o proveedor equivalente).
            </PolicyText>

            <SectionTitle>3. Finalidades y bases legales</SectionTitle>
            <List>
              <ListItem><strong>Prestación del servicio (contrato)</strong>: sincronizar y mostrar tu información financiera y mantener tu cuenta.</ListItem>
              <ListItem><strong>Asistente de IA (contrato/interés legítimo)</strong>: procesar tus consultas para categorizar y asistir.</ListItem>
              <ListItem><strong>Mejora y seguridad (interés legítimo)</strong>: métricas internas, diagnóstico y prevención de fraude/abuso.</ListItem>
              <ListItem><strong>Notificaciones (consentimiento)</strong>: avisos y recordatorios mediante Firebase Cloud Messaging (FCM).</ListItem>
              <ListItem><strong>Comunicaciones comerciales (consentimiento)</strong>: solo si te suscribes; puedes darte de baja en cualquier momento.</ListItem>
            </List>

            <SectionTitle>4. Proveedores y transferencias</SectionTitle>
            <PolicyText>Usamos proveedores de confianza y limitamos los datos a lo necesario:</PolicyText>
            <List>
              <ListItem><strong>Autenticación y notificaciones</strong>: Google <em>Firebase Auth</em> (Google/Apple Sign-In y email + contraseña) y <em>Firebase Cloud Messaging</em>.</ListItem>
              <ListItem><strong>Suscripciones y compras</strong>: <em>RevenueCat</em> (pagos vía Google Play / App Store).</ListItem>
              <ListItem><strong>Procesamiento de IA</strong>: <em>OpenAI</em>, <em>Azure OpenAI</em> y/o <em>DeepSeek</em> (solo procesamiento de texto; no para publicidad; no entrenamiento de modelos con nuestros datos).</ListItem>
              <ListItem><strong>Almacenamiento de imágenes de perfil</strong>: <em>Bunny.net</em>.</ListItem>
              <ListItem><strong>Infraestructura</strong>: servidores en <em>Linode</em> y <em>Microsoft Azure</em>.</ListItem>
              <ListItem><strong>Publicidad (solo plan gratuito)</strong>: <em>Unity Ads</em>, <em>AppLovin MAX</em> y/o <em>Google AdMob</em> (anuncios NO PERSONALIZADOS; ver Sección 10).</ListItem>
            </List>
            <PolicyText>
              <strong>Identificador de publicidad (AAID/IDFA)</strong>. Nuestros SDKs de anuncios (p. ej., Unity Ads, AppLovin MAX, Google AdMob) pueden acceder al identificador de publicidad del dispositivo para mostrar anuncios no personalizados, aplicar limitación de frecuencia, medir rendimiento y prevenir fraude/abuso. No usamos el identificador para anuncios personalizados en el plan gratuito. Puedes restablecer o limitar el identificador en la configuración de tu dispositivo.
            </PolicyText>
            <PolicyText>
              Los proveedores de IA procesan las entradas del usuario únicamente para generar respuestas para la función solicitada. No permitimos el uso de tus datos para publicidad, perfilado o entrenamiento de modelos, y utilizamos controles contractuales y técnicos para hacer cumplir esto.
            </PolicyText>
            <PolicyText>
              Los datos pueden transferirse fuera de tu país/EEE. Cuando es necesario, usamos <em>Cláusulas Contractuales Tipo (SCCs)</em> u otras garantías adecuadas.
            </PolicyText>

            <SectionTitle>5. Conservación</SectionTitle>
            <PolicyText>
              Conservamos tus datos mientras tu cuenta esté activa. Tras la eliminación, copias de seguridad y logs se conservan durante <strong>60 días</strong> por seguridad y cumplimiento; después se purgan de forma irreversible, salvo obligación legal.
            </PolicyText>

            <SectionTitle>6. Seguridad</SectionTitle>
            <PolicyText>
              Cifrado en tránsito y en reposo, controles de acceso y prácticas de desarrollo seguro. Ningún sistema es infalible; si una brecha te afectara de forma material, te lo notificaremos conforme a la ley.
            </PolicyText>

            <SectionTitle>7. Tus derechos</SectionTitle>
            <PolicyText>
              Según tu jurisdicción (p. ej., GDPR/CCPA), puedes solicitar acceso, rectificación, supresión, portabilidad,
              limitación, oposición y retirar el consentimiento. Escríbenos a <strong>privacy@xadevs.com</strong> desde el
              correo asociado a tu cuenta.
            </PolicyText>

            <SectionTitle>8. Eliminación de cuenta y exportación</SectionTitle>
            <PolicyText>
              Puedes solicitar la eliminación permanente de tu cuenta desde la app (o por email). Los datos activos se borran; las copias de seguridad/logs siguen la conservación indicada en la Sección 5. 
              Las tiendas pueden conservar datos de facturación según sus propias políticas.
            </PolicyText>

            <SectionTitle>9. Edad</SectionTitle>
            <PolicyText>
              VolpeTrack está destinada a mayores de 18 años. No recopilamos datos personales de menores de 18 años de forma consciente. Si descubrimos que hemos recogido datos personales de un menor, los eliminaremos y podremos cancelar la cuenta.
            </PolicyText>

            <SectionTitle>10. Anuncios, planes y consentimiento</SectionTitle>
            <PolicyText>
              Planes: <strong>Gratuito (Ad-Supported)</strong> muestra publicidad; <strong>Premium Pro</strong> y <strong>Founder Pioneer</strong> no muestran publicidad.
            </PolicyText>
            <PolicyText>
              <strong>Anuncios no personalizados (solo plan gratuito)</strong>. Mostramos anuncios no personalizados. Los socios publicitarios pueden procesar el identificador de publicidad del dispositivo (GAID/IDFA), región aproximada de IP, señales básicas de dispositivo/app y eventos de anuncios agregados únicamente para limitación de frecuencia, medición y prevención de fraude, según las políticas de las plataformas. No habilitamos el seguimiento entre aplicaciones ni la personalización basada en intereses en el plan gratuito. En el EEE/Reino Unido, si se requiere consentimiento para operaciones específicas de SDK de anuncios, lo solicitaremos en la app. Actualizar a Premium Pro o Founder Pioneer deshabilita los SDK de anuncios.
            </PolicyText>

            <SectionTitle>11. Permisos (Android)</SectionTitle>
            <PolicyText>
              Solicitamos únicamente los permisos necesarios para las funciones que eliges:
            </PolicyText>
            <List>
              <ListItem><strong>INTERNET</strong>: conectar a nuestra API y servicios de terceros.</ListItem>
              <ListItem><strong>POST_NOTIFICATIONS</strong>: notificaciones opcionales de la app (puedes deshabilitarlas).</ListItem>
              <ListItem><strong>RECORD_AUDIO</strong>: voz a texto opcional para el asistente de IA (solo cuando presionas el micrófono).</ListItem>
              <ListItem><strong>CAMERA</strong>: captura opcional de foto de perfil.</ListItem>
              <ListItem><strong>READ_EXTERNAL_STORAGE</strong> (solo Android 12 o anterior, con maxSdkVersion=32): seleccionar una foto de perfil de tu galería en versiones antiguas de Android. En Android 13+, usamos el Selector de Fotos de Android y no requerimos acceso de lectura a la galería.</ListItem>
              <ListItem><strong>WAKE_LOCK</strong> y <strong>VIBRATE</strong>: entrega confiable y retroalimentación del usuario para las notificaciones.</ListItem>
              <ListItem><strong>com.google.android.gms.permission.AD_ID</strong>: identificador de publicidad utilizado por los SDKs de anuncios en el plan gratuito para anuncios no personalizados, medición y prevención de fraude.</ListItem>
            </List>
            <PolicyText>
              No escaneamos tu biblioteca de fotos; solo procesamos la imagen individual que seleccionas para tu avatar.
            </PolicyText>

            <SectionTitle>12. Compras y suscripciones</SectionTitle>
            <PolicyText>
              Los pagos se gestionan mediante Google Play / App Store a través de <strong>RevenueCat</strong>. No almacenamos datos de tarjeta en nuestros servidores.
              Los cargos aparecen como <strong>"XADevs LLC"</strong>. Puedes gestionar o cancelar la suscripción desde los ajustes de tu cuenta en la tienda.
            </PolicyText>

            <SectionTitle>13. No vendemos tus datos</SectionTitle>
            <PolicyText>
              No vendemos tus datos personales. Para residentes en California, puedes ejercer "No vender/compartir" eligiendo anuncios no personalizados y/o escribiendo a <strong>privacy@xadevs.com</strong>.
            </PolicyText>

            <SectionTitle>14. Cambios a esta Política</SectionTitle>
            <PolicyText>
              Podemos actualizar esta Política. Notificaremos cambios relevantes dentro de la app y, cuando corresponda, por correo. La fecha al inicio refleja la última revisión.
            </PolicyText>

            <SectionTitle>15. Contacto</SectionTitle>
            <PolicyText>
              Privacidad: <strong>privacy@xadevs.com</strong><br/>
              Soporte: <strong>support@xadevs.com</strong><br/>
              Web: <strong>https://xadevs.com</strong>
            </PolicyText>
          </PolicyContainer>
          )}
        </ContentWrapper>
      </PageContainer>
    </>
  );
};

export default VolpeTrackPrivacyPolicy;
